import React from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { TrendingUp, TrendingDown, ShoppingBag, LifeBuoy, Calendar, AlertTriangle, Package, Users } from "lucide-react";

const PERIODS = [[7, "7 zile"], [30, "30 zile"], [90, "90 zile"]];

export default function AdminDashboard() {
    const { user } = useAuth();
    const [kpi, setKpi] = React.useState(null);
    const [period, setPeriod] = React.useState(30);

    // Installers don't have access to KPI — redirect them to their main view (Tickete).
    const isInstaller = user?.role === "installer";

    React.useEffect(() => {
        if (isInstaller) return;
        api.get(`/admin/dashboard/kpi?period_days=${period}`).then(r => setKpi(r.data));
    }, [period, isInstaller]);

    if (isInstaller) return <Navigate to="/admin/tickets" replace />;
    if (!kpi) return <div className="text-muted-foreground">Se încarcă dashboard-ul...</div>;

    const maxRev = Math.max(...(kpi.daily_revenue || []).map(d => d.revenue), 1);
    const TrendIcon = (kpi.revenue_pct ?? 0) >= 0 ? TrendingUp : TrendingDown;
    const trendColor = (kpi.revenue_pct ?? 0) >= 0 ? "text-primary" : "text-destructive";

    return (
        <div data-testid="admin-dashboard">
            <div className="flex items-start justify-between mb-6">
                <div><div className="overline mb-1">Administrare</div><h1 className="font-heading text-3xl">Dashboard</h1></div>
                <div className="flex gap-1 bg-muted rounded-xl p-1">
                    {PERIODS.map(([d, l]) => (
                        <button key={d} onClick={() => setPeriod(d)} className={`px-3 py-1.5 text-sm rounded-lg ${period === d ? "bg-card shadow-sm" : "text-muted-foreground"}`} data-testid={`period-${d}`}>{l}</button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                <KpiCard label="Venit perioadă" value={`${kpi.revenue.toFixed(2)} RON`}
                         pct={kpi.revenue_pct} Icon={TrendIcon} iconClassName={trendColor} testid="kpi-revenue" />
                <KpiCard label="Comenzi" value={kpi.orders_count}
                         pct={kpi.orders_count_pct} Icon={ShoppingBag} testid="kpi-orders" />
                <KpiCard label="Valoare medie comandă" value={`${kpi.aov.toFixed(2)} RON`} Icon={TrendingUp} testid="kpi-aov" />
                <KpiCard label="Tickete deschise" value={kpi.open_tickets} Icon={LifeBuoy}
                         link="/admin/tickets" testid="kpi-tickets" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                <KpiCard label="Programări în așteptare" value={kpi.pending_appointments} Icon={Calendar} link="/admin/appointments" />
                <KpiCard label="Produse cu stoc redus" value={kpi.low_stock_products} Icon={Package} link="/admin/inventory?only_low=1" />
                <KpiCard label="Produse importante (stoc 0)" value={kpi.critical_zero_products}
                         Icon={AlertTriangle} iconClassName={kpi.critical_zero_products ? "text-destructive" : ""}
                         link="/admin/inventory?only_critical=1" />
                <KpiCard label="Clienți" value="Vezi toți" Icon={Users} link="/admin/clients" />
            </div>

            <div className="card-soft p-5 mb-6">
                <h3 className="font-heading text-lg mb-3">Venit zilnic · ultimele {period} zile</h3>
                <div className="flex items-end gap-1 h-40">
                    {(kpi.daily_revenue || []).map((d, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center group relative">
                            <div className="w-full bg-primary/70 hover:bg-primary rounded-t-md"
                                 style={{ height: `${d.revenue ? Math.max(8, (d.revenue / maxRev) * 100) : 2}%` }} />
                            <div className="hidden group-hover:block absolute -top-8 text-xs whitespace-nowrap bg-foreground text-background px-2 py-1 rounded-md">{d.revenue.toFixed(0)} RON</div>
                        </div>
                    ))}
                </div>
                <div className="flex justify-between text-[10px] text-muted-foreground mt-2">
                    <span>{kpi.daily_revenue?.[0]?.date}</span>
                    <span>{kpi.daily_revenue?.[kpi.daily_revenue.length - 1]?.date}</span>
                </div>
            </div>

            <div className="card-soft p-5">
                <h3 className="font-heading text-lg mb-3">Top 5 produse (după venit)</h3>
                {(kpi.top_products || []).length === 0 ? <p className="text-sm text-muted-foreground">Fără comenzi plătite în perioadă.</p> :
                    <div className="divide-y divide-border">
                        {kpi.top_products.map((p, i) => (
                            <div key={p.product_id} className="py-2 flex items-center gap-3">
                                <div className="w-6 text-center font-heading text-xl text-muted-foreground">{i + 1}</div>
                                <div className="flex-1">{p.name || p.product_id}</div>
                                <div className="text-sm text-muted-foreground">{p.quantity} buc</div>
                                <div className="font-medium ml-4">{p.total.toFixed(2)} RON</div>
                            </div>
                        ))}
                    </div>}
            </div>
        </div>
    );
}

function KpiCard({ label, value, pct, Icon, iconClassName = "", link, testid }) {
    const body = (
        <div className={`card-soft p-4 ${link ? "hover:border-primary cursor-pointer" : ""}`} data-testid={testid}>
            <div className="flex items-center justify-between mb-1">
                <div className="text-xs text-muted-foreground">{label}</div>
                {Icon && <Icon className={`h-4 w-4 text-muted-foreground ${iconClassName}`} />}
            </div>
            <div className="font-heading text-2xl">{value}</div>
            {pct !== null && pct !== undefined && (
                <div className={`text-xs mt-1 ${pct >= 0 ? "text-primary" : "text-destructive"}`}>{pct >= 0 ? "↑" : "↓"} {Math.abs(pct)}% vs perioada anterioară</div>
            )}
        </div>
    );
    return link ? <Link to={link}>{body}</Link> : body;
}
